import React, {type ReactNode, useState} from 'react';

import {SelectBaseRTK} from 'app/components/general/SelectBase/SelectBaseRTK';
import {useCurrentUserState} from 'app/current-user/api';
import {convertLocationParamsToFetchParams} from 'app/user/actions';
import {useCreateUser, useUsers} from 'app/user/api';
import {gt} from 'app/utils/i18n';

import {InviteUserButton, InviteUserForm} from './InviteUserForm';
import {SelectUsersOption} from './SelectUsersOption';
import {
  SelectUsersMultiValueLabel,
  SelectUsersSingleValueLabel,
} from './SelectUsersValueLabel';

import type {ActionMeta} from 'app/components/general/SweetReactSelect';
import type {NewUserType, UsersLocationParamsType} from 'app/user/types';
import type {BasicUserSerializedType, UserSerializedType} from 'sweet-types';

export const selectCommonFixedSelectProps = {
  components: {
    NoOptionsMessage: InviteUserButton,
    MultiValueLabel: SelectUsersMultiValueLabel,
    SingleValue: SelectUsersSingleValueLabel,
    Option: SelectUsersOption,
  },
};

type SharedUserLogicOwnProps<U> = {
  placeholder?: string;
  onInvite: (user: U | UserSerializedType) => unknown;
  children: (props: {
    placeholder: string;
    showInviteForm: boolean;
    selectProps: {
      extraProps: {
        canInvite: boolean;
        onInvite: (name: string) => unknown;
      };
    };
    children: JSX.Element | null;
  }) => JSX.Element;
};

function SharedUserLogic<U extends BasicUserSerializedType>({
  children,
  placeholder,
  onInvite: onInviteProp,
}: SharedUserLogicOwnProps<U>): JSX.Element {
  const user = useCurrentUserState().data!;
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteName, setInviteName] = useState('');

  const [createUser] = useCreateUser();

  const onInvite = (name: string) => {
    setInviteName(name);
    setShowInviteForm(true);
  };

  const handleInviteUser = (userData: NewUserType) => {
    return doCreateUser(userData).then((user) => {
      setInviteName('');
      setShowInviteForm(false);
      onInviteProp(user);
      return user;
    });
  };

  const doCreateUser = (userData: NewUserType): Promise<UserSerializedType> =>
    createUser(userData).unwrap();

  return children({
    placeholder:
      placeholder != null ? placeholder : gt("Type each teammate's name"),
    showInviteForm: showInviteForm,
    selectProps: {
      extraProps: {
        canInvite: user.is_manager,
        onInvite: onInvite,
      },
    },
    children: showInviteForm ? (
      <div style={{position: 'relative'}}>
        <InviteUserForm
          name={inviteName}
          onSubmit={handleInviteUser}
          hideInviteForm={() => setShowInviteForm(false)}
        />
      </div>
    ) : null,
  });
}

type CommonProps = {
  selectLimit?: number;
  selectLimitText?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  lazyFetch?: boolean;
  params?: UsersLocationParamsType;
  components?: Record<string, unknown>;
};

type SingleProps<U> = CommonProps & {
  selected: U | null;
  onChange: (
    user: U | UserSerializedType,
    meta: ActionMeta<U | UserSerializedType>
  ) => Promise<unknown>;
};
export function SelectUsersSingle<U extends BasicUserSerializedType>({
  placeholder,
  ...other
}: SingleProps<U>): JSX.Element {
  return (
    <SharedUserLogic<U>
      placeholder={placeholder}
      onInvite={(user) =>
        other.onChange(user, {action: 'select-option', option: user})
      }
    >
      {({showInviteForm, ...commonProps}) => (
        <SelectBaseRTK
          isMulti={false}
          isRTKFetcher={true}
          useDataHook={useUsers}
          menuIsOpen={showInviteForm ? false : undefined}
          paramsConverter={convertLocationParamsToFetchParams}
          {...selectCommonFixedSelectProps}
          {...other}
          {...commonProps}
        />
      )}
    </SharedUserLogic>
  );
}

type MultiItemProps<U> = CommonProps & {
  selected: Array<U>;
  onChange: (
    users: Array<U | UserSerializedType>,
    meta: ActionMeta<U | UserSerializedType>
  ) => Promise<unknown>;
};
export function SelectUsersMulti<U extends BasicUserSerializedType>({
  placeholder,
  ...other
}: MultiItemProps<U>): JSX.Element {
  return (
    <SharedUserLogic<U>
      placeholder={placeholder}
      onInvite={(user) =>
        other.onChange([...other.selected, user], {
          action: 'select-option',
          option: user,
        })
      }
    >
      {({showInviteForm, ...commonProps}) => (
        <SelectBaseRTK
          isMulti={true}
          isRTKFetcher={true}
          useDataHook={useUsers}
          menuIsOpen={showInviteForm ? false : undefined}
          paramsConverter={convertLocationParamsToFetchParams}
          {...selectCommonFixedSelectProps}
          {...other}
          {...commonProps}
        />
      )}
    </SharedUserLogic>
  );
}
